import styled from '@emotion/styled';
import {
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  FlashOnOutlined,
  FormatListBulletedOutlined,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useProductCoddingDelete from 'api/productDetail/useProductCoddingDelete/useProductCoddingDelete';
import CodingCreateDialog from 'components/shared/BranchSystemCreate/Dialog';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import CoddingMappingModal from 'components/shared/Modals/productsList/CoddingMapping/CoddingMapping';
import CreateProductCodding from 'components/shared/Modals/productsList/CreateProductCodding/CreateProductCodding';
import ManualProductCodding from 'components/shared/Modals/productsList/ProductCoddingEditModal/ProductCoddingEditModal';
import { CODE_LOGIC } from 'components/shared/productDetail/AccordionCodingList/AccordionCodingList';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { useCallback, useMemo, useState } from 'react';

const coddingPermissions =
  permissions.general.categories.globalSettings.productManagement.productList
    .detail.codding;

const BranchCodingList = ({ codingList, refreshBranchData, branchGuid }) => {
  const { can } = usePermissions();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCoding, setSelectedCoding] = useState(null);
  const [selectedSystemEditGuid, setSelectedSystemEditGuid] = useState(null);
  const [editManual, setEditManual] = useState(null);
  const [editMapping, setEditMapping] = useState(null);
  const [isCreateCoddingOpen, setIsCreateCoddingOpen] = useState(false);

  const { deleteLoading, deleteRequest } = useProductCoddingDelete();

  const deleteItem = useCallback(
    row => () => {
      setSelectedCoding(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const onDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    refreshBranchData();
  };

  const deleteHandle = id => {
    deleteRequest(id, onDeleteSuccess);
  };

  const isMenuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event, coding) => {
    setAnchorEl(event.currentTarget);
    setSelectedCoding(coding);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  function handleEdit(codingItem) {
    console.log(codingItem);
    switch (codingItem.type) {
      case '0':
        setSelectedSystemEditGuid(codingItem.guid);
        return;
      case '1':
        setEditManual(codingItem);
        return;
      case '2':
        setEditMapping(codingItem);
        return;
      default:
        return '';
    }
  }

  function handleEditManual() {
    setEditManual(null);
    refreshBranchData();
  }

  function handleUpdateMapping() {
    setEditMapping(null);
    refreshBranchData();
  }

  function onCoddingCreated() {
    refreshBranchData();
    setIsCreateCoddingOpen(false);
  }

  const triggerBatchItem = useCallback(row => () => {}, []);
  const showCodeListItem = useCallback(row => () => {}, []);
  const downloadCodeListItem = useCallback(row => () => {}, []);
  const showSerialListItem = useCallback(row => () => {}, []);
  const downloadSerialListItem = useCallback(row => () => {}, []);

  const menuItems = useMemo(
    () => [
      {
        label: 'حذف',
        permission: coddingPermissions.delete,
        icon: DeleteOutline,
        color: 'error',
        visible: row => row?.type !== '0',
        onClick: row => {
          handleCloseMenu();
          deleteItem(row)();
        },
      },
      {
        label: 'ویرایش',
        permission: coddingPermissions.edit,
        icon: EditOutlined,
        color: 'warning',
        visible: row => row?.logic === CODE_LOGIC.SKU || !row?.logic,
        onClick: row => {
          handleCloseMenu();
          handleEdit(row);
        },
      },
      {
        label: 'تریگر',
        permission: coddingPermissions.batch.trigger,
        icon: FlashOnOutlined,
        color: 'secondary',
        visible: row => row?.logic === CODE_LOGIC.BatchNumber,
        onClick: row => {
          handleCloseMenu();
          triggerBatchItem(row);
        },
      },
      {
        label: 'نمایش لیست کد',
        permission: coddingPermissions.batch.view,
        icon: FormatListBulletedOutlined,
        color: 'info',
        visible: row => row?.logic === CODE_LOGIC.BatchNumber,
        onClick: row => {
          handleCloseMenu();
          showCodeListItem(row);
        },
      },
      {
        label: 'دانلود لیست کد',
        permission: coddingPermissions.batch.download,
        icon: DownloadOutlined,
        color: 'success',
        visible: row => row?.logic === CODE_LOGIC.BatchNumber,
        onClick: row => {
          handleCloseMenu();
          downloadCodeListItem(row);
        },
      },
      {
        label: 'نمایش لیست سریال ها',
        permission: coddingPermissions.serial.view,
        icon: FormatListBulletedOutlined,
        color: 'info',
        visible: row => row?.logic === CODE_LOGIC.SerialNumber,
        onClick: row => {
          handleCloseMenu();
          downloadSerialListItem(row);
        },
      },
      {
        label: 'دانلود لیست سریال ها',
        permission: coddingPermissions.serial.download,
        icon: DownloadOutlined,
        color: 'success',
        visible: row => row?.logic === CODE_LOGIC.SerialNumber,
        onClick: row => {
          handleCloseMenu();
          downloadSerialListItem(row);
        },
      },
      {
        label: 'کد پیش‌فرض',
        permission: coddingPermissions.setDefault,
        icon: FormatListBulletedOutlined,
        color: 'primary',
        visible: () => true,
        onClick: row => {
          handleCloseMenu();
          showCodeListItem(row);
        },
      },
    ],
    [
      deleteItem,
      triggerBatchItem,
      showCodeListItem,
      downloadCodeListItem,
      downloadSerialListItem,
    ],
  );

  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter(
        item => item.visible(selectedCoding) && can(item.permission),
      ),
    [menuItems, selectedCoding, can],
  );

  const canCreate = can(coddingPermissions.create);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 4,
          mb: 2,
          paddingX: '16px',
        }}
      >
        <Typography variant="h6" fontSize={18} fontWeight={600}>
          لیست کدینگ
        </Typography>
        {canCreate ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setIsCreateCoddingOpen(true)}
          >
            <AddIcon />
            افزودن کدینگ
          </Button>
        ) : null}
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  borderColor: 'rgba(100, 100, 100, .2)',
                  background: 'rgba(180, 180, 180, 0.05)',
                },
              }}
            >
              <TableCell
                align="right"
                sx={{ width: { xs: 'auto', lg: '450px', xl: '550px' } }}
              >
                نام
              </TableCell>
              <TableCell align="right">کدینگ</TableCell>
              <TableCell align="left" width={100}>
                عملیات ها
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {codingList?.map((coding, index) => {
              const rowHasActions = menuItems.some(
                item => item.visible(coding) && can(item.permission),
              );

              return (
                <TableRow key={coding.guid || index}>
                  <TableCell align="right">{coding.display}</TableCell>
                  <TableCell align="right">{coding.codding}</TableCell>
                  <TableCell align="right" width={180}>
                    {rowHasActions ? (
                      <ActionContainer>
                        <div>
                          <IconButton
                            id={`${coding.display}-button`}
                            aria-controls={
                              isMenuOpen ? `${coding.display}-menu` : undefined
                            }
                            aria-haspopup="true"
                            aria-expanded={isMenuOpen ? 'true' : undefined}
                            onClick={event => handleOpenMenu(event, coding)}
                          >
                            <MenuIcon />
                          </IconButton>
                        </div>
                      </ActionContainer>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {visibleMenuItems.map(item => {
            const Icon = item.icon;

            return (
              <MenuItem
                key={item.label}
                onClick={() => item.onClick(selectedCoding)}
              >
                <Action>
                  <Typography fontSize={14}>{item.label}</Typography>
                  <Icon fontSize="small" color={item.color} />
                </Action>
              </MenuItem>
            );
          })}
        </Menu>
      </TableContainer>

      {can(coddingPermissions.delete) ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          title={selectedCoding?.display}
          locationId={selectedCoding?.guid}
          deleteLoading={deleteLoading}
          onDelete={() => deleteHandle(selectedCoding?.guid)}
        />
      ) : null}

      {can(coddingPermissions.edit) ? (
        <ManualProductCodding
          open={!!editManual}
          handleClose={() => setEditManual(null)}
          chosenProduct={editManual}
          onUpdate={handleEditManual}
        />
      ) : null}

      {can(coddingPermissions.edit) ? (
        <CoddingMappingModal
          open={!!editMapping}
          onClose={() => setEditMapping(null)}
          onUpdate={handleUpdateMapping}
          guid={editMapping?.id}
        />
      ) : null}

      {can(coddingPermissions.edit) ? (
        <CodingCreateDialog
          open={!!selectedSystemEditGuid}
          onClose={() => setSelectedSystemEditGuid(null)}
          handleConfirm={() => setSelectedSystemEditGuid(null)}
          selected={selectedSystemEditGuid}
        />
      ) : null}

      {canCreate ? (
        <CreateProductCodding
          open={isCreateCoddingOpen}
          onClose={() => setIsCreateCoddingOpen(false)}
          selectedGuid={branchGuid}
          onSuccess={onCoddingCreated}
        />
      ) : null}
    </Box>
  );
};

export default BranchCodingList;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Action = styled(Box)({
  paddingTop: '0.3rem',
  paddingBottom: '0.3rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
});
